import * as Sentry from "@sentry/nextjs";
import type { z } from "zod";

/**
 * Проверка формы сообщения хаба — режим предупреждения (KAN-988, KAN-1375).
 *
 * Несовпадение со схемой уходит в GlitchTip с именем хаба и события, а
 * обработчик всё равно получает payload как есть: строгая проверка на первом же
 * выкате отбросила бы то, что сейчас «как-то работает».
 *
 * Почему не `handleSilentError`: он пишет только во внутренний лог и в консоль
 * вне прода — в GlitchTip не уходит ничего. С 21.09 по 25.09 режим предупреждения
 * match-live из-за этого молчал, и решение «отбрасывать или нет» принимать было
 * не по чему.
 *
 * Отчёт — один на пару «хаб + событие» за сессию: сломанный контракт шлёт
 * каждое сообщение, и без этого поток в десятки событий в секунду стал бы
 * потоком отчётов. Группировка в GlitchTip — по тому же ключу (fingerprint).
 */
const reported = new Set<string>();

export function checkHubPayload(
  hub: string,
  event: string,
  schema: z.ZodType,
  payload: unknown,
): void {
  const parsed = schema.safeParse(payload);
  if (parsed.success) return;
  const key = `${hub}:${event}`;
  if (reported.has(key)) return;
  reported.add(key);
  Sentry.captureMessage(`Контракт хаба ${hub}: событие ${event} не совпало со схемой`, {
    level: "warning",
    tags: { stage: "hub_contract", hub, hub_event: event },
    // Только путь и код расхождения: значения полей в отчёт не кладём.
    extra: {
      issues: parsed.error.issues.slice(0, 5).map((i) => ({ path: i.path.join("."), code: i.code, message: i.message })),
    },
    fingerprint: ["hub-contract", hub, event],
  });
}

/** Сброс памяти об отправленных отчётах — для тестов. */
export function resetHubContractReports(): void {
  reported.clear();
}
